import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Card, CardContent } from "@/components/ui/card";
import { BookOpen, PlayCircle, Lightbulb } from "lucide-react";
import { Tool } from "@/types";
import { RichTextEditor } from "./RichTextEditor";

interface GuideSectionProps {
  tool?: Tool;
  isEditing?: boolean;
  onGuideChange?: (content: string) => void;
}

const getVideoEmbedUrl = (url: string): string => {
  try {
    const videoId = url.includes('youtube.com') 
      ? new URL(url).searchParams.get('v')
      : url.includes('youtu.be')
      ? url.split('/').pop()
      : '';
    
    return videoId 
      ? `https://www.youtube.com/embed/${videoId}`
      : url;
  } catch {
    return url;
  }
};

export const GuideSection = ({ tool, isEditing, onGuideChange }: GuideSectionProps) => {
  return (
    <div className="mb-12 animate-fade-in">
      <h2 className="text-2xl font-bold mb-6 text-primary font-arabic">دليل الاستخدام</h2>
      
      <Accordion type="single" collapsible className="w-full space-y-4">
        <AccordionItem value="guide-1" className="border rounded-lg bg-card">
          <AccordionTrigger className="px-4 hover:no-underline">
            <div className="flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-primary" />
              <span className="font-arabic">كيفية استخدام الأداة</span>
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-4 pb-4">
            {isEditing ? (
              <div className="min-h-[300px] border rounded-lg p-4 bg-background">
                <RichTextEditor
                  value={tool?.usage_guide || ''}
                  onChange={onGuideChange || (() => {})}
                  placeholder="اكتب دليلاً مفصلاً لكيفية استخدام الأداة..."
                />
              </div>
            ) : (
              <div 
                className="prose dark:prose-invert max-w-none font-arabic"
                dangerouslySetInnerHTML={{ __html: tool?.usage_guide || "" }}
              />
            )}
          </AccordionContent>
        </AccordionItem>

        {(isEditing || tool?.tutorial_video_url) && (
          <AccordionItem value="guide-2" className="border rounded-lg bg-card">
            <AccordionTrigger className="px-4 hover:no-underline">
              <div className="flex items-center gap-2">
                <PlayCircle className="w-5 h-5 text-primary" />
                <span className="font-arabic">فيديو تعليمي</span>
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-4 pb-4">
              <div className="grid gap-4">
                {tool?.tutorial_video_url ? (
                  <div className="aspect-video rounded-lg overflow-hidden">
                    <iframe
                      src={getVideoEmbedUrl(tool.tutorial_video_url)}
                      className="w-full h-full"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  </div>
                ) : (
                  <Card>
                    <CardContent className="p-4">
                      <p className="text-muted-foreground font-arabic">
                        {isEditing ? "أدخل رابط الفيديو التعليمي" : "لا يوجد فيديو تعليمي"}
                      </p>
                    </CardContent>
                  </Card>
                )}
              </div>
            </AccordionContent>
          </AccordionItem>
        )}

        {(isEditing || (tool?.examples && tool.examples.length > 0)) && (
          <AccordionItem value="guide-3" className="border rounded-lg bg-card">
            <AccordionTrigger className="px-4 hover:no-underline">
              <div className="flex items-center gap-2">
                <Lightbulb className="w-5 h-5 text-primary" />
                <span className="font-arabic">أمثلة عملية</span>
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-4 pb-4 font-arabic">
              <div className="space-y-4">
                {tool?.examples?.map((example, index) => (
                  <Card key={index}>
                    <CardContent className="p-4">
                      <h4 className="font-bold mb-2">{example.title}</h4>
                      <p className="text-muted-foreground">
                        {example.description}
                      </p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </AccordionContent>
          </AccordionItem>
        )}
      </Accordion>
    </div>
  );
};